/*
 * Lemonade Training Oy copy rights all right reserved.
 * Time zone arithmetic shared by mobile, web and functions: calendar-day keys,
 * a zone's midnights and wall clocks, and the city a zone is named after.
 * Intl only, no imports. Nothing here throws on any input: functions call it
 * from plain JavaScript and message wording falls back to the gym on null.
 */

/** "YYYY-MM-DD" on some zone's calendar. */
export type CalendarDateKey = string;
/** Epoch milliseconds or a Date. */
export type Instant = number | Date;

export type ZonedWallClock = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

/** endMs is the next midnight minus 1 ms, like functions/todayWindow.js. */
export type DayWindow = { startMs: number; endMs: number };

export type WallClockResult =
  { instant: number } | { reason: "gap" | "invalid" };

/** Longest zone id any helper accepts; also the stored-zone limit in firestore.rules. */
export const MAX_TIME_ZONE_LENGTH = 64;

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const MAX_CACHED_FORMATTERS = 256;
const KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const HHMM_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// Explicit en-US so a device locale with non-Latin digits can never break the keys.
const PARTS: Intl.DateTimeFormatOptions & { hourCycle: "h23" } = {
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};

const INVALID_READING: ZonedWallClock = {
  year: NaN,
  month: NaN,
  day: NaN,
  hour: NaN,
  minute: NaN,
  second: NaN,
};

// Zone strings reach the server from clients, so only successes are cached.
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: unknown): Intl.DateTimeFormat | null {
  if (
    typeof zone !== "string" ||
    zone.length < 1 ||
    zone.length > MAX_TIME_ZONE_LENGTH
  ) {
    return null;
  }
  const cached = formatters.get(zone);
  if (cached) return cached;
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat("en-US", { ...PARTS, timeZone: zone });
  } catch {
    return null;
  }
  if (formatters.size >= MAX_CACHED_FORMATTERS) formatters.clear();
  formatters.set(zone, formatter);
  return formatter;
}

export function _resetZoneTimeCacheForTests(): void {
  formatters.clear();
}

export function _zoneTimeCacheSizeForTests(): number {
  return formatters.size;
}

function toMs(at: unknown): number {
  if (typeof at === "number") return at;
  return at instanceof Date ? at.getTime() : NaN;
}

function read(formatter: Intl.DateTimeFormat, ms: number): ZonedWallClock {
  const parts: Record<string, number> = {};
  try {
    for (const part of formatter.formatToParts(ms)) {
      parts[part.type] = Number(part.value);
    }
  } catch {
    return INVALID_READING;
  }
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    // Some ICU builds render midnight as 24.
    hour: parts.hour % 24,
    minute: parts.minute,
    second: parts.second,
  };
}

function asUtcMs(w: ZonedWallClock): number {
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
}

/** The zone's offset from UTC at an instant, in ms. */
function offsetAt(formatter: Intl.DateTimeFormat, ms: number): number {
  return asUtcMs(read(formatter, ms)) - ms;
}

const pad = (n: number, width: number) => String(n).padStart(width, "0");

function keyOf(year: number, month: number, day: number): CalendarDateKey {
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

function keyFromUtcMs(ms: number): CalendarDateKey {
  const d = new Date(ms);
  return keyOf(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

function parseKey(value: unknown): { y: number; m: number; d: number } | null {
  if (typeof value !== "string" || !KEY_PATTERN.test(value)) return null;
  const y = Number(value.slice(0, 4));
  const m = Number(value.slice(5, 7));
  const d = Number(value.slice(8, 10));
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y &&
    t.getUTCMonth() === m - 1 &&
    t.getUTCDate() === d
    ? { y, m, d }
    : null;
}

/**
 * Constructs a formatter instead of consulting Intl.supportedValuesOf, which on
 * Node 20 omits valid ids such as Europe/Kyiv, Asia/Kolkata and UTC.
 */
export function isUsableTimeZone(zone: unknown): zone is string {
  return formatterFor(zone) !== null;
}

export function canonicalTimeZone(zone: unknown): string | null {
  const formatter = formatterFor(zone);
  return formatter === null ? null : formatter.resolvedOptions().timeZone;
}

export function wallClockParts(
  at: Instant,
  zone: unknown,
): ZonedWallClock | null {
  const formatter = formatterFor(zone);
  if (formatter === null) return null;
  const reading = read(formatter, toMs(at));
  return Number.isNaN(reading.year) ? null : reading;
}

export function calendarDateKey(
  at: Instant,
  zone: unknown,
): CalendarDateKey | null {
  const reading = wallClockParts(at, zone);
  if (reading === null) return null;
  const key = keyOf(reading.year, reading.month, reading.day);
  return isCalendarDateKey(key) ? key : null;
}

export function isCalendarDateKey(value: unknown): value is CalendarDateKey {
  return parseKey(value) !== null;
}

export function addDaysToKey(
  key: unknown,
  days: number,
): CalendarDateKey | null {
  const p = parseKey(key);
  if (p === null || !Number.isInteger(days)) return null;
  const next = keyFromUtcMs(Date.UTC(p.y, p.m - 1, p.d + days));
  return isCalendarDateKey(next) ? next : null;
}

/** 0 = Sunday. */
export function weekdayOfKey(key: unknown): number | null {
  const p = parseKey(key);
  return p === null ? null : new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay();
}

export function weekStartKey(
  key: unknown,
  weekStartsOn = 1,
): CalendarDateKey | null {
  const weekday = weekdayOfKey(key);
  if (
    weekday === null ||
    !Number.isInteger(weekStartsOn) ||
    weekStartsOn < 0 ||
    weekStartsOn > 6
  ) {
    return null;
  }
  return addDaysToKey(key, -((weekday - weekStartsOn + 7) % 7));
}

/**
 * Whether `key` can be today somewhere on Earth at `nowMs` (UTC-12..UTC+14),
 * widened by `skewMs` for client clock drift. Pure arithmetic: the today
 * callables use it to bound a client-supplied day.
 */
export function isPlausibleTodayKey(
  key: unknown,
  nowMs: number,
  skewMs = 10 * MINUTE_MS,
): boolean {
  if (
    !isCalendarDateKey(key) ||
    !Number.isFinite(nowMs) ||
    !Number.isFinite(skewMs) ||
    skewMs < 0
  ) {
    return false;
  }
  const earliest = keyFromUtcMs(nowMs - 12 * HOUR_MS - skewMs);
  const latest = keyFromUtcMs(nowMs + 14 * HOUR_MS + skewMs);
  return earliest <= key && key <= latest;
}

/**
 * The earliest instant the wall clock reads `hour:minute` on day `p`, or null
 * when that time is skipped. Offsets are sampled around the estimate (any
 * offset size straddles a nearby DST change) and two days either side of the
 * naive time (a whole repeated or skipped day at a date-line change).
 */
function firstInstantAt(
  formatter: Intl.DateTimeFormat,
  p: { y: number; m: number; d: number },
  hour: number,
  minute: number,
): number | null {
  const naive = Date.UTC(p.y, p.m - 1, p.d, hour, minute);
  const estimate = naive - offsetAt(formatter, naive);
  const probes = [
    estimate - 3 * HOUR_MS,
    estimate + 3 * HOUR_MS,
    naive - 48 * HOUR_MS,
    naive + 48 * HOUR_MS,
  ];
  const offsets = probes.map((t) => naive - offsetAt(formatter, t));
  const candidates = offsets.filter((c, i) => offsets.indexOf(c) === i);
  const matches = candidates.filter((candidate) => {
    const w = read(formatter, candidate);
    return (
      w.year === p.y &&
      w.month === p.m &&
      w.day === p.d &&
      w.hour === hour &&
      w.minute === minute
    );
  });
  return matches.length === 0 ? null : Math.min(...matches);
}

/**
 * First instant of the day in `zone`: the first of a repeated midnight, or
 * the end of the gap when midnight is skipped.
 */
export function zoneMidnight(key: unknown, zone: unknown): number | null {
  const p = parseKey(key);
  const formatter = formatterFor(zone);
  if (p === null || formatter === null) return null;
  const first = firstInstantAt(formatter, p, 0, 0);
  if (first !== null) return first;
  // Midnight was skipped: binary-search the first whole second that reads
  // this day. Two days either side covers any offset change, date line included.
  const dayUtc = Date.UTC(p.y, p.m - 1, p.d);
  let lo = dayUtc - 48 * HOUR_MS;
  let hi = dayUtc + 48 * HOUR_MS;
  while (hi - lo > 1000) {
    const mid = lo + Math.floor((hi - lo) / 2000) * 1000;
    if (asUtcMs(read(formatter, mid)) < dayUtc) lo = mid;
    else hi = mid;
  }
  return hi;
}

/** Null for an invalid key or zone, and for a day the zone skipped entirely. */
export function dayWindowForKey(key: unknown, zone: unknown): DayWindow | null {
  const startMs = zoneMidnight(key, zone);
  if (startMs === null) return null;
  const nextMs = zoneMidnight(addDaysToKey(key, 1), zone);
  return nextMs === null || nextMs <= startMs
    ? null
    : { startMs, endMs: nextMs - 1 };
}

/**
 * The instant a wall clock reads `hhmm` on `key` in `zone`. A time that occurs
 * twice resolves to its first occurrence; a skipped time is a gap, never shifted.
 */
export function wallClockToInstant(
  key: unknown,
  hhmm: unknown,
  zone: unknown,
): WallClockResult {
  const p = parseKey(key);
  const formatter = formatterFor(zone);
  if (
    p === null ||
    formatter === null ||
    typeof hhmm !== "string" ||
    !HHMM_PATTERN.test(hhmm)
  ) {
    return { reason: "invalid" };
  }
  const instant = firstInstantAt(
    formatter,
    p,
    Number(hhmm.slice(0, 2)),
    Number(hhmm.slice(3, 5)),
  );
  return instant === null ? { reason: "gap" } : { instant };
}

/** Whether two zones' wall clocks read differently (to the minute) at any of `instants`. */
export function clocksDiffer(
  zoneA: unknown,
  zoneB: unknown,
  instants: readonly Instant[],
): boolean {
  const a = formatterFor(zoneA);
  const b = formatterFor(zoneB);
  if (a === null || b === null || zoneA === zoneB || !Array.isArray(instants)) {
    return false;
  }
  for (const at of instants) {
    const ms = toMs(at);
    const wallA = asUtcMs(read(a, ms));
    const wallB = asUtcMs(read(b, ms));
    if (
      !Number.isNaN(wallA) &&
      Math.floor(wallA / MINUTE_MS) !== Math.floor(wallB / MINUTE_MS)
    ) {
      return true;
    }
  }
  return false;
}

const CITY_ALIASES = new Map<string, string>([
  ["Kiev", "Kyiv"],
  ["Calcutta", "Kolkata"],
  ["Saigon", "Ho Chi Minh"],
  ["Godthab", "Nuuk"],
  ["Rangoon", "Yangon"],
  ["Katmandu", "Kathmandu"],
  ["Asmera", "Asmara"],
  ["Faeroe", "Faroe"],
  ["Enderbury", "Kanton"],
  ["Truk", "Chuuk"],
  ["Ponape", "Pohnpei"],
  ["Coral_Harbour", "Atikokan"],
]);

const lastSegment = (zone: string) => zone.slice(zone.lastIndexOf("/") + 1);

/**
 * The city a zone is named after, for "(Helsinki time)" wording; null for UTC,
 * GMT, Etc/* and offset ids. Engines disagree on canonical spellings (Node 20
 * turns Europe/Kyiv into Europe/Kiev), so legacy names map to modern ones.
 */
export function zoneCity(zone: unknown): string | null {
  if (typeof zone !== "string") return null;
  const canonical = canonicalTimeZone(zone);
  if (
    canonical === null ||
    canonical.indexOf("/") < 0 ||
    canonical.startsWith("Etc/")
  ) {
    return null;
  }
  const segment = lastSegment(canonical);
  return (
    CITY_ALIASES.get(lastSegment(zone)) ??
    CITY_ALIASES.get(segment) ??
    segment.replace(/_/g, " ")
  );
}
